import { useState, useEffect } from 'react';
import { staffApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/DataTable';
import { toast } from 'sonner';
import { Plus, Pencil, User } from 'lucide-react';
import { format } from 'date-fns';

const ROLES = ['admin', 'manager', 'surveyor'];
const REGIONS = ['Essex', 'Suffolk', 'Kent', 'Norfolk', 'Cambridgeshire', 'London', 'Yorkshire', 'Lancashire', 'Midlands', 'Wales', 'South West', 'National'];
const SPECIALTIES = ['Structural', 'Flood Damage', 'Fire Damage', 'Storm Damage', 'Condition Surveys', 'Claim Validation', 'Repair Costings'];

export default function StaffPage() {
  const [staff, setStaff] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'surveyor',
    regions: [],
    specialties: [],
    is_available: true,
    max_workload: 10,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const data = await staffApi.getAll();
      setStaff(data);
    } catch (error) {
      toast.error('Failed to load staff');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', email: '', password: '', phone: '', role: 'surveyor',
      regions: [], specialties: [], is_available: true, max_workload: 10,
    });
    setEditingStaff(null);
  };

  const openDialog = (member = null) => {
    if (member) {
      setEditingStaff(member);
      setFormData({
        name: member.name || '',
        email: member.email || '',
        password: '',
        phone: member.phone || '',
        role: member.role || 'surveyor',
        regions: member.regions || [],
        specialties: member.specialties || [],
        is_available: member.is_available !== false,
        max_workload: member.max_workload || 10,
      });
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStaff) {
        const { password, ...updateData } = formData;
        await staffApi.update(editingStaff.id, updateData);
        toast.success('Staff member updated');
      } else {
        if (!formData.password) {
          toast.error('Password is required for new staff');
          return;
        }
        await staffApi.create(formData);
        toast.success('Staff member created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save staff member');
    }
  };

  const toggleRegion = (region) => {
    const current = formData.regions || [];
    if (current.includes(region)) {
      setFormData({ ...formData, regions: current.filter(r => r !== region) });
    } else {
      setFormData({ ...formData, regions: [...current, region] });
    }
  };

  const toggleSpecialty = (specialty) => {
    const current = formData.specialties || [];
    if (current.includes(specialty)) {
      setFormData({ ...formData, specialties: current.filter(s => s !== specialty) });
    } else {
      setFormData({ ...formData, specialties: [...current, specialty] });
    }
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone', render: (val) => val || '-' },
    { key: 'role', label: 'Role', render: (val) => (
      <Badge className={
        val === 'admin' ? 'bg-purple-100 text-purple-800' :
        val === 'manager' ? 'bg-blue-100 text-blue-800' :
        'bg-green-100 text-green-800'
      }>{val}</Badge>
    )},
    { key: 'regions', label: 'Regions', render: (val) => (
      <div className="flex flex-wrap gap-1">
        {val?.slice(0, 2).map((r, i) => (
          <Badge key={i} variant="outline" className="text-xs">{r}</Badge>
        ))}
        {val?.length > 2 && <Badge variant="outline" className="text-xs">+{val.length - 2}</Badge>}
      </div>
    )},
    { key: 'current_workload', label: 'Workload', render: (val, row) => (
      <span className={val >= row.max_workload ? 'text-red-600 font-medium' : ''}>
        {val || 0}/{row.max_workload || 10}
      </span>
    )},
    { key: 'is_available', label: 'Available', render: (val) => (
      <Badge className={val ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
        {val ? 'Yes' : 'No'}
      </Badge>
    )},
  ];

  const actions = [
    { label: 'Edit', icon: Pencil, onClick: openDialog },
  ];

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Staff Management</h1>
          <p className="text-sm text-stone-500 mt-1">Manage team members and surveyors</p>
        </div>
        <Button onClick={() => openDialog()} className="bg-emerald-900 hover:bg-emerald-800" data-testid="add-staff-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Staff Member
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={staff}
        actions={actions}
        isLoading={isLoading}
        emptyMessage="No staff members found."
      />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="staff-dialog">
          <DialogHeader>
            <DialogTitle>{editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="staff-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val })}>
                  <SelectTrigger data-testid="staff-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={!!editingStaff}
                  data-testid="staff-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="staff-phone-input"
                />
              </div>
            </div>

            {!editingStaff && (
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingStaff}
                  data-testid="staff-password-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Regions</Label>
              <div className="flex flex-wrap gap-2">
                {REGIONS.map((r) => (
                  <Badge
                    key={r}
                    variant="outline"
                    className={`cursor-pointer ${formData.regions?.includes(r) ? 'bg-emerald-100 border-emerald-500' : ''}`}
                    onClick={() => toggleRegion(r)}
                  >
                    {r}
                  </Badge>
                ))}
              </div>
            </div>

            {formData.role === 'surveyor' && (
              <>
                <div className="space-y-2">
                  <Label>Specialties</Label>
                  <div className="flex flex-wrap gap-2">
                    {SPECIALTIES.map((s) => (
                      <Badge
                        key={s}
                        variant="outline"
                        className={`cursor-pointer ${formData.specialties?.includes(s) ? 'bg-sky-100 border-sky-500' : ''}`}
                        onClick={() => toggleSpecialty(s)}
                      >
                        {s}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Max Workload</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.max_workload}
                      onChange={(e) => setFormData({ ...formData, max_workload: parseInt(e.target.value) })}
                      data-testid="staff-workload-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Available for Jobs</Label>
                    <div className="flex items-center space-x-2 pt-2">
                      <Switch
                        checked={formData.is_available}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_available: checked })}
                        data-testid="staff-available-switch"
                      />
                      <Label className="font-normal">{formData.is_available ? 'Yes' : 'No'}</Label>
                    </div>
                  </div>
                </div>
              </>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800" data-testid="staff-submit-btn">
                {editingStaff ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
